/**
 * ProposalShareModal — single source of truth for the
 * "Share Proposal" handoff used after saving in the Estimator and from
 * the Proposals list (per-row 📤 action + Quick View Share button).
 *
 * Owns its own message builder + WhatsApp / SMS / Email / Copy / Open
 * actions. Embeds EditableExpiry so the operator can bump the validity
 * window right before sending.
 *
 * The handoff message is the customer's to read, so it is written in the
 * country's own language (Danish for Denmark, read from country_config.locale)
 * and falls back to English. No extra UI: the message follows the country.
 */

import EditableExpiry from './EditableExpiry';
import { useConfig } from '../../../../contexts/ConfigContext';
import ShareModal from '../../../../components/ShareModal';

type ShareLocale = 'en' | 'da';

const SHARE_MESSAGES: Record<ShareLocale, {
  build: (firstName: string, url: string) => string;
  subject: (ref: string | null | undefined) => string;
}> = {
  en: {
    build: (firstName, url) => `Hi ${firstName || 'there'}, thank you for your interest in CleanMyBed.\n\nI've prepared a personalised service proposal for you, take a look here:\n${url}\n\nLet me know if you have any questions. Looking forward to helping you create a cleaner, healthier sleep environment.`,
    subject: (ref) => `Your CleanMyBed proposal${ref ? ` · ${ref}` : ''}`,
  },
  da: {
    build: (firstName, url) => `Hej${firstName ? ` ${firstName}` : ''}, tak for din interesse i CleanMyBed.\n\nJeg har forberedt et personligt servicetilbud til dig. Tag et kig her:\n${url}\n\nLad mig endelig vide, hvis du har nogen spørgsmål. Jeg ser frem til at hjælpe dig med at skabe et renere og sundere sovemiljø.`,
    subject: (ref) => `Dit CleanMyBed-tilbud${ref ? ` · ${ref}` : ''}`,
  },
};

interface ProposalShareModalProps {
  estimateId: string;
  referenceCode: string | null | undefined;
  proposalUrl: string | null | undefined;
  expiresAt: string | null | undefined;
  customerName?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
  /** Shown on the Estimator post-save flow — sends the operator back to the proposals list. */
  showDoneButton?: boolean;
  onDone?: () => void;
  onClose: () => void;
}

export default function ProposalShareModal({
  estimateId,
  referenceCode,
  proposalUrl,
  expiresAt,
  customerName,
  customerEmail,
  customerPhone,
  showDoneButton = false,
  onDone,
  onClose,
}: ProposalShareModalProps) {
  const { countryConfig } = useConfig();

  // Message follows the country's own language. Denmark → Danish, else English.
  const primaryLang = ((countryConfig?.locale || 'en').split('-')[0] ?? 'en').toLowerCase();
  const lang: ShareLocale = primaryLang === 'da' ? 'da' : 'en';

  return (
    <ShareModal
      title="Share Proposal"
      url={proposalUrl}
      recipientName={customerName}
      recipientEmail={customerEmail}
      recipientPhone={customerPhone}
      buildMessage={(firstName, url) => SHARE_MESSAGES[lang].build(firstName, url)}
      emailSubject={SHARE_MESSAGES[lang].subject(referenceCode)}
      linkLabel="Proposal Link"
      phoneFieldLabel="Customer WhatsApp / mobile number"
      footerExtra={
        showDoneButton ? (
          <button type="button" className="btn btn-secondary" onClick={onDone}>
            ✓ Done
          </button>
        ) : undefined
      }
      onClose={onClose}
    >
      {referenceCode && <div className="badge badge-neutral">{referenceCode}</div>}
      {expiresAt && (
        <div className="form-group">
          <EditableExpiry estimateId={estimateId} expiresAt={expiresAt} />
        </div>
      )}
    </ShareModal>
  );
}
